/**
 * Key Created Modal
 *
 * Shows the newly created API key with copy functionality.
 * Warns user that this is the only time they'll see the full key.
 *
 * @module components/keys/KeyCreatedModal
 */

import { useState } from 'react';
import { Modal, Button, Alert } from '@/components/ui';
import { Check, Copy } from 'lucide-react';
import { copyToClipboard } from '@/lib/utils';

// =============================================================================
// Types
// =============================================================================

export interface KeyCreatedModalProps {
  open: boolean;
  onClose: () => void;
  /** The full API key (shown only once) */
  apiKey: string;
  /** The key label */
  label: string;
}

// =============================================================================
// Component
// =============================================================================

export function KeyCreatedModal({
  open,
  onClose,
  apiKey,
}: KeyCreatedModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const success = await copyToClipboard(apiKey);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="API Key Created"
      closeOnBackdropClick={false}
      closeOnEscape={false}
      footer={
        <div className="flex items-center justify-between w-full">
          <span className={`text-sm text-success-600 transition-opacity ${copied ? 'opacity-100' : 'opacity-0'}`}>
            Copied to clipboard!
          </span>
          <Button onClick={onClose}>Done</Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Warning */}
        <Alert variant="warning" title="Copy your API key now!">
          You won't be able to see it again. Store it in a secure location.
        </Alert>

        {/* Key Display */}
        <div className="flex items-center gap-2 p-3 bg-gray-900 rounded-lg font-mono text-sm text-gray-100 overflow-x-auto">
          <code className="flex-1 break-all">{apiKey}</code>
          <button
            onClick={handleCopy}
            className="flex-shrink-0 p-2 rounded hover:bg-gray-700 transition-colors"
            aria-label={copied ? 'Copied!' : 'Copy to clipboard'}
          >
            {copied ? (
              <Check className="h-5 w-5 text-success-400" />
            ) : (
              <Copy className="h-5 w-5 text-gray-400" />
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}